import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EnvironmentInjector, EventEmitter, inject, Input, OnInit, Output, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActionButton, AddressBook, AddressBookQuery } from '@fgb/core';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { map } from 'rxjs';
import { FGBValidators } from '@fgb/portal-component-library/src/lib/shared/validators';

enum RECIPIENT_NAVIGATION {
  NEW_RECIPIENT,
  EXISTING_CONTACTS
};

/**
 * Reusable component recipient form where you can assign a dynamic action to the form. 
 * This component can be used other than ticket transfer if there are any particular use cases that require recipient form.
 * 
 * @see ActionButton
 */
@Component({
  selector: 'fgbcl-recipient-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FGBSharedModule, LoadingModule],
  templateUrl: './recipient-form.component.html',
  styleUrl: './recipient-form.component.scss'
})
export class RecipientFormComponent implements OnInit {
  @Input() wscPrefix: string = 'fgbcl.recipient.form';
  @Input({ required: true }) action: ActionButton; // Dynamic action which will be invoked on form submit
  @Output() onCancel: EventEmitter<void> = new EventEmitter<void>();
  currentNav = RECIPIENT_NAVIGATION.NEW_RECIPIENT;
  navigation = RECIPIENT_NAVIGATION;
  newRecipientForm: FormGroup;
  contactList: AddressBook[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(private fb: FormBuilder, private injector: EnvironmentInjector, private addressBookQuery: AddressBookQuery) {}

  ngOnInit(): void {
    this._initializeForm();
    
    this.addressBookQuery.selectAllContacts()
      .pipe(
          map((contactsList) => contactsList.sort((a, b) => a.FullName.localeCompare(b.FullName))), 
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(contacts => {
          this.contactList = contacts || [];
        });
  }

  /**
   * If form is valid, invoke the action
   */
  submit(): void{
    this.newRecipientForm.markAllAsTouched();

    if( this.newRecipientForm.valid ){
      this.invoke(this.newRecipientForm.value);
    }
  }

  private invoke(data: any): void{
    runInInjectionContext(this.injector, () => {
      this.action.errorMessage = '';
      // Invoke action
      this.action.invoke(data);
    });
  }

  /**
   * Navigate to recipient nav pages
   */
  navigateTo(nav: RECIPIENT_NAVIGATION): void{
    this.currentNav = nav;
    // Re-initialize form to reset values to default
    this._initializeForm();
  }

  /**
   * On existing contact selection, populate the form with selected contact details.
   */
  onChange(contactId: number): void{
    const contact = this.getContactById(+contactId);
    this.newRecipientForm.get('Id')?.patchValue(contact?.Id);
    this.newRecipientForm.get('FullName')?.patchValue(contact?.FullName);
    this.newRecipientForm.get('EmailAddress')?.patchValue(contact?.EmailAddress);
  }

  /**
   * Returns contact details by Id
   */
  private getContactById(id: number): AddressBook | undefined{
    return this.contactList.find(contact => contact.Id === id);
  }

  /**
   * Initialize form with default values and validators
   */
  private _initializeForm(): void{
    this.newRecipientForm = this.fb.group({
      Id: [null], // In case it's needed we need to pass the contact id in the payload so this component can be reusable in other scenarios
      FullName: ['', [Validators.required, FGBValidators.Empty, Validators.pattern("^[a-zA-Z ,.'-]+$")]],
      EmailAddress: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      saveRecipient: [false]
    });
  }
} 